import { AttachFile, InsertDriveFile } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import {
  athenaApi,
  useDeleteApiAttachmentsByIdMutation,
} from '../../../../../app/athenaApiGen';
import { useDeliverableModalContext } from '../DeliverableModalContext';
import { formatFileSize } from '../formatFileSize';

const UploaderField = () => {
  const { uploadedFiles, setUploadedFiles, savedFiles, setSavedFiles } =
    useDeliverableModalContext();
  const dispatch = useDispatch();
  const [deleteAttachment] = useDeleteApiAttachmentsByIdMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setUploadedFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleDelete = (fileName: string) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileName),
    );
  };

  const handleDeleteSaved = async (attachmentId: string) => {
    try {
      await deleteAttachment({ id: attachmentId }).unwrap();
      setSavedFiles((prev) =>
        prev.filter((f) => f.attachmentId !== attachmentId),
      );
      // Refetch the deliverable so its attachments array is fresh next
      // time it's read (ViewPanel, list rows, etc.).
      dispatch(athenaApi.util.invalidateTags(['Deliverables']));
    } catch (err) {
      console.error('Remove attachment failed:', err);
      setErrorMessage('Failed to remove attachment. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderRadius: 2,
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'primary.light' : 'background.paper',
          transition: 'border-color 0.3s, background-color 0.3s',
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <Typography variant="h6" color="primary">
            Drop the files here...
          </Typography>
        ) : (
          <Box>
            <AttachFile sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No files selected yet
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Drag 'n' drop some files here, or click to select files
            </Typography>
          </Box>
        )}
      </Box>

      {(savedFiles.length > 0 || uploadedFiles.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            mt: 2,
            overflow: 'auto',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            Selected Files ({savedFiles.length + uploadedFiles.length})
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              height: '100%',
              mt: 2,
            }}
          >
            <List sx={{ padding: 0 }}>
              {savedFiles.map((file) => (
                <ListItem
                  disablePadding
                  key={file.attachmentId}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="remove attachment"
                      onClick={() =>
                        file.attachmentId &&
                        handleDeleteSaved(file.attachmentId)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <InsertDriveFile sx={{ color: 'text.secondary', mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={
                      file.fileSize ? formatFileSize(file.fileSize) : undefined
                    }
                  />
                </ListItem>
              ))}
              {uploadedFiles.map((file, index) => (
                <ListItem
                  disablePadding
                  key={`upload-${index}`}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDelete(file.name)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <InsertDriveFile sx={{ color: 'text.secondary', mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      )}
      <Snackbar
        open={errorMessage !== null}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UploaderField;
